import { Link } from 'react-router-dom';
import { formatDateRange, formatDayPhrase } from '@bookingdei/i18n/format-date';
import { localiseDigits } from '@bookingdei/i18n/format-number';
import { statusColours } from '../../theme/tokens';
import { brandAssets } from '../../theme/brand-assets';
import { useLanguage } from '../../shell/language-context';
import { OfflineChip } from '../../shell/offline-chip';
import { templateFacts } from '../bookings/booking-facts';
import type { Fact } from '../bookings/booking-facts';
import { HERO_LINE, heroBusy, heroState } from './home-model';
import { HomeIcon } from './home-icons';
import type { Booking, Template } from '../../mock/seeds/schema-types';
import type { MessageKey } from '@bookingdei/i18n/message-key';

// The next-booking hero (customer gap §A.3 H2; Home.dc.html L51-115): a tone strip that
// says where the booking stands (pulsing dot, scan bar while the customer is waiting on
// someone else, "Resubmit proof" when the business rejected the proof, the offline
// chip), then eyebrow · service · place, the big "when" tile, the at-a-glance facts,
// the booking code (tap to copy) beside the QR button, and Details / Directions / Call.
export function HomeHero({
  booking,
  template,
  now,
  onCopy,
  onQr,
}: {
  booking: Booking;
  template: Template | undefined;
  now: Date;
  onCopy: () => void;
  onQr: () => void;
}) {
  const { t, lang } = useLanguage();
  const state = heroState(booking, now);
  const tone = statusColours('booking_status', state);
  const busy = heroBusy(state);
  const service = booking.items?.[0]?.name_snapshot ?? '';
  const biz = booking.business;
  const bizName = (lang === 'bn' ? biz?.name_bn : undefined) ?? biz?.name ?? '';
  const place = [bizName, biz?.area_name].filter(Boolean).join(' — ');
  const start = booking.starts_at ? new Date(booking.starts_at) : null;
  const end = booking.ends_at ? new Date(booking.ends_at) : null;
  const ranged = template?.scheduling_model === 'date_range' && start && end;
  const when = ranged
    ? formatDateRange(lang, start, end)
    : start
      ? formatDayPhrase(lang, start, now)
      : '';
  const facts = glanceFacts(booking, template, lang, t, now);
  const detailsHref = `/bookings?b=${encodeURIComponent(booking.code)}`;
  const mapQuery = biz?.address ?? place;
  const mapHref = biz?.map_link ?? `https://maps.google.com/?q=${encodeURIComponent(mapQuery)}`;

  return (
    <section className="hm-hero" data-motion="">
      <div className="hm-strip" style={{ background: tone.bg }}>
        {busy && (
          <span className="hm-strip__scan" aria-hidden="true">
            <span
              data-motion=""
              style={{
                background: `linear-gradient(90deg, transparent, ${tone.dot}, transparent)`,
              }}
            />
          </span>
        )}
        <span
          className="hm-strip__dot"
          data-motion=""
          style={{ color: tone.dot, background: tone.dot }}
          aria-hidden="true"
        />
        <span className="hm-strip__text" style={{ color: tone.fg }}>
          {t(HERO_LINE[state])}
        </span>
        {state === 'rejected' && (
          <Link className="hm-strip__cta" to={detailsHref}>
            {t('user.home.hero.resubmit')}
          </Link>
        )}
        <OfflineChip />
      </div>

      <div className="hm-hero__body">
        <div className="hm-hero__head" data-motion="">
          <span className="hm-kicker">{t('user.home.hero.eyebrow')}</span>
          <span className="hm-hero__service">{service}</span>
          <span className="hm-hero__place">
            <HomeIcon name="pin" />
            {place}
          </span>
        </div>

        <div className="hm-when" data-motion="">
          <span className="hm-when__rail" data-motion="" aria-hidden="true" />
          <span className="hm-when__sheen" data-motion="" aria-hidden="true" />
          <HomeIcon name="clock" size={20} className="hm-when__icon" motion />
          <span className="hm-when__text">{when}</span>
        </div>

        {(booking.serial_no != null || facts.length > 0) && (
          <div className="hm-glance-wrap" data-motion="">
            <div className="hm-glance">
              {booking.serial_no != null && (
                <div className="hm-glance__cell">
                  <span className="hm-glance__k">{t('user.bk.fact.serial')}</span>
                  <span className="hm-glance__serial">
                    {localiseDigits(lang, booking.serial_no)}
                  </span>
                </div>
              )}
              {facts.map((f) => (
                <div key={f.label} className="hm-glance__cell">
                  <span className="hm-glance__k">{f.label}</span>
                  <span className="hm-glance__v">{f.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="hm-code-row" data-motion="">
          <div className="hm-code-col">
            <span className="hm-kicker">{t('user.home.hero.code')}</span>
            <button
              type="button"
              className="hm-code"
              title={t('user.home.hero.copy_hint')}
              onClick={onCopy}
            >
              {booking.code}
              <HomeIcon name="copy" className="hm-code__icon" />
            </button>
          </div>
          <button
            type="button"
            className="hm-qr-btn"
            aria-label={t('user.home.hero.qr')}
            title={t('user.home.hero.qr_tap')}
            onClick={onQr}
          >
            <img className="hm-qr-btn__mark" src={brandAssets.mark} alt="" />
            <img className="hm-qr-btn__mark-rev" src={brandAssets.markReversed} alt="" />
          </button>
        </div>

        <div className="hm-actions" data-motion="">
          <Link className="hm-act hm-act--primary" to={detailsHref}>
            {t('user.home.hero.details')}
            <HomeIcon name="arrow" />
          </Link>
          <a className="hm-act" href={mapHref} target="_blank" rel="noreferrer">
            <HomeIcon name="pin" className="hm-act__icon" />
            {t('user.home.hero.directions')}
          </a>
          <a className="hm-act" href={biz?.phone ? `tel:${biz.phone}` : undefined}>
            <HomeIcon name="phone" className="hm-act__icon" />
            {t('user.home.hero.call')}
          </a>
        </div>
      </div>
    </section>
  );
}

// At-a-glance facts (design `glance` part = vDetail): a stay shows its check-in and
// check-out, then every answered template field (the professional, the room, the
// batch…). The serial number has its own big tile.
function glanceFacts(
  b: Booking,
  template: Template | undefined,
  lang: 'en' | 'bn',
  t: (k: MessageKey) => string,
  now: Date,
): Fact[] {
  const out: Fact[] = [];
  if (template?.scheduling_model === 'date_range') {
    if (b.starts_at)
      out.push({
        label: t('user.bk.fact.checkin'),
        value: formatDayPhrase(lang, new Date(b.starts_at), now),
      });
    if (b.ends_at)
      out.push({
        label: t('user.bk.fact.checkout'),
        value: formatDayPhrase(lang, new Date(b.ends_at), now),
      });
  }
  return [...out, ...templateFacts(b, template, lang, t)];
}

// No live booking (Home.dc.html L117-126): the faded mark, "No upcoming booking", the
// empty-account line when there has never been a booking, and "Find a service".
export function HomeHeroEmpty({
  emptyAccount,
  findHref,
}: {
  emptyAccount: boolean;
  findHref: string;
}) {
  const { t } = useLanguage();
  return (
    <section className="hm-none" data-motion="">
      <img className="hm-none__mark" src={brandAssets.mark} alt="" />
      <span className="hm-none__title">{t('user.home.hero.none')}</span>
      {emptyAccount && <span className="hm-none__line">{t('user.home.hero.empty_line')}</span>}
      <Link className="hm-pill" to={findHref} state={{ from: '/' }}>
        {t('user.home.hero.find')}
      </Link>
    </section>
  );
}
